import React, { useEffect, useMemo, useState } from "react";
import { usePohrebka2 } from "../../context/Pohrebka2Context";
import { CILOVA_MISTA } from "../../data/cilovaMista";

const FIRMA_ADRESA = "Pod Skládkou 525, Český Krumlov";

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 24,
  padding: 24,
  boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: 16,
};

const labelStyle = {
  display: "block",
  fontSize: 14,
  fontWeight: 700,
  color: "#111827",
  marginBottom: 8,
};

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 14,
  border: "1px solid #d1d5db",
  fontSize: 15,
  background: "#fff",
  outline: "none",
  boxSizing: "border-box",
};

const readOnlyStyle = {
  ...inputStyle,
  background: "#f3f4f6",
};

const inputReadonlyStyle = {
  ...inputStyle,
  background: "#f3f4f6",
};

const textareaStyle = {
  ...inputStyle,
  minHeight: 110,
  resize: "vertical",
};

const infoBoxStyle = {
  marginBottom: 18,
  padding: 14,
  borderRadius: 16,
  background: "#f9fafb",
  border: "1px solid #e5e7eb",
};

const secondaryBtn = {
  padding: "12px 16px",
  borderRadius: 14,
  border: "1px solid #d1d5db",
  background: "#fff",
  color: "#111827",
  fontWeight: 800,
  cursor: "pointer",
};

function normalizeText(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function readToken() {
  try {
    return (
      localStorage.getItem("pohrebka_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("accessToken") ||
      ""
    );
  } catch {
    return "";
  }
}


function getDefaultBezObraduScenar(prevoz = {}, vstupni = {}) {
  const existing = String(prevoz?.bezObraduScenar || "").trim();
  if (existing) return existing;

  const adresa = String(prevoz?.adresaOdvozu || vstupni?.adresaUmrti || "").toLowerCase();

  if (
    adresa.includes("nemocnic") ||
    adresa.includes("nemocnice český krumlov") ||
    adresa.includes("český krumlov")
  ) {
    return "ck_nemocnice";
  }

  return "doma_nebo_mimo_ck_nemocnici";
}

function getDefaultOdvozPlaceFromScenario(scenarUmrti = "") {
  const scenar = String(scenarUmrti || "").trim();

  if (scenar === "nemocnice_ck") {
    return {
      mistoOdvozu: "Nemocnice Český Krumlov",
      adresaOdvozu: "Nemocniční 429, 381 01 Český Krumlov",
    };
  }

  if (scenar === "wagnerka") {
    return {
      mistoOdvozu: "Wagnerka",
      adresaOdvozu: "Kaplická 423, 381 01 Český Krumlov",
    };
  }

  return {};
}

function apiUrl(path) {
  const base = String(import.meta?.env?.VITE_API_BASE_URL || "").replace(/\/+$/, "");
  return path.startsWith("/") ? `${base}${path}` : `${base}/${path}`;
}

function getEtapaValue(prevoz, key, fallback = "") {
  return ((prevoz?.etapy || {})[key]) || fallback;
}


function getTransportPresetFromPrubeh({ typZakazky = "", prubehKey = "", scenarUmrti = "" } = {}) {
  const typ = String(typZakazky || "").trim();
  const prubeh = String(prubehKey || "").trim();
  const scenar = String(scenarUmrti || "").trim();

  if (typ === "kremace_bez_obradu") {
    return {
      dopravaMode: prubeh === "bez_obradu_urna" ? "combo_cb" : "pausal_cb",
      bezObraduScenar:
        scenar === "nemocnice_ck"
          ? "ck_nemocnice"
          : "doma_nebo_mimo_ck_nemocnici",
    };
  }

  if (typ === "kremace") {
    if (prubeh === "cb_krematorium_rozlouceni") {
      return {
        dopravaMode: "km",
        cilPrevozuTyp: "krematorium",
        cilPrevozuNazev: "Krematorium České Budějovice",
        cilPrevozuAdresa: "Pražská 2275/108, 370 10 České Budějovice",
      };
    }

    return {
      dopravaMode: "km",
      cilPrevozuTyp: "krematorium",
      cilPrevozuNazev: "Krematorium České Budějovice",
      cilPrevozuAdresa: "Pražská 2275/108, 370 10 České Budějovice",
    };
  }

  if (typ === "pohreb") {
    return {
      dopravaMode: "km",
      cilPrevozuTyp: "hrbitov",
      cilPrevozuNazev: "Hřbitov Český Krumlov",
      cilPrevozuAdresa: "Hřbitovní 149, 381 01 Český Krumlov",
    };
  }

  if (typ === "obrad_s_urnou") {
    return {
      dopravaMode: "pausal_ck",
    };
  }

  if (typ === "alternativa") {
    return {
      dopravaMode: "km",
    };
  }

  return {};
}

export default function Pohrebka2PrevozForm() {
  const { order, patchSection, activeOrderId } = usePohrebka2();
  const prevoz = order?.prevoz || {};

  useEffect(() => {
    const handler = (e) => {
      const date = String(e?.detail?.date || "").slice(0, 10);
      const time = String(e?.detail?.time || "").slice(0, 5);
      const typZakazky = String(e?.detail?.typZakazky || "").trim();
      const misto = String(e?.detail?.misto || "").trim();
      const poznamka = String(e?.detail?.poznamka || "").trim();

      if (typZakazky !== "prevoz") return;

      patchSection("prevoz", {
        ...(date ? { datumPrevozu: date } : {}),
        ...(time ? { casPrevozu: time } : {}),
        ...((misto && misto !== "__manual__") ? { mistoOdvozu: misto } : {}),
        ...(poznamka ? { poznamka } : {}),
      });
    };

    window.addEventListener("pohrebka2-calendar-create-date", handler);
    return () => window.removeEventListener("pohrebka2-calendar-create-date", handler);
  }, [patchSection]);
  const zaklad = order?.zaklad || {};
  const vstupni = order?.vstupniInformace || {};
  const obrad = order?.obrad || {};
  const vyjezd = order?.vyjezd || {};
  const jeSamostatnyPrevoz = zaklad?.typZakazky === "prevoz";
  const jeVyjezd = zaklad?.typZakazky === "vyjezd";

  const vyjezdStorageType = String(vyjezd?.storageType || "").trim();
  const vyjezdRezimRaw = String(zaklad?.vyjezdRezim || vyjezd?.vyjezdRezim || "").trim();

  const vyjezdRezim =
    vyjezdRezimRaw ||
    (vyjezdStorageType === "soudni_pitva"
      ? "soudni_pitva"
      : vyjezdStorageType === "zdravotni_pitva"
        ? "zdravotni_pitva"
        : "");

  const jeSoudniPitva = vyjezdRezim === "soudni_pitva";
  const jeZdravotniPitva = vyjezdRezim === "zdravotni_pitva";

  useEffect(() => {
    const scenarUmrti = String(zaklad?.scenarUmrti || "").trim();
    if (!scenarUmrti) return;
    if (String(prevoz?.mistoOdvozu || "").trim() || String(prevoz?.adresaOdvozu || "").trim()) return;

    const preset = getDefaultOdvozPlaceFromScenario(scenarUmrti);
    if (!preset || !Object.keys(preset).length) return;

    patchSection("prevoz", preset);
  }, [
    zaklad?.scenarUmrti,
    prevoz?.mistoOdvozu,
    prevoz?.adresaOdvozu,
    patchSection,
  ]); // AUTO_ODVOZ_FROM_SCENAR_UMRTI


  const [kmInfo, setKmInfo] = useState({ state: "idle", msg: "" });

  const setField = (name, value) => {
    patchSection("prevoz", { [name]: value });
  };

  const setEtapaField = (name, value) => {
    patchSection("prevoz", {
      etapy: {
        ...(prevoz?.etapy || {}),
        [name]: value,
      },
    });
  };

  const odvozAdresa =
    prevoz.adresaOdvozu ||
    vstupni.adresaUmrti ||
    "";

  const navratNaFirmu = prevoz.navratNaFirmu !== false;

  const mistoObradu =
    obrad?.misto ||
    obrad?.hrbitov ||
    "";

  const adresaObradu =
    obrad?.mistoAdresa ||
    obrad?.hrbitovAdresa ||
    "";

  const adresaLednice = prevoz.cilPrevozuAdresa || prevoz.cilPrevozuNazev || "";
  const adresaKrematoria = obrad?.krematoriumAdresa || obrad?.krematorium || "";

  const cilovaAdresaPodleTypu =
    String(zaklad?.typZakazky || "").trim() === "pohreb"
      ? (obrad?.hrbitovAdresa || adresaObradu || "")
      : String(zaklad?.typZakazky || "").trim() === "kremace" || String(zaklad?.typZakazky || "").trim() === "kremace_bez_obradu"
        ? (obrad?.krematoriumAdresa || adresaKrematoria || "")
        : (adresaObradu || adresaKrematoria || "");

  const automatickaCilovaAdresa =
    prevoz.cilPrevozuAdresa ||
    cilovaAdresaPodleTypu ||
    "";

  const jeKremaceSObradem =
    String(zaklad?.typZakazky || "").trim() === "kremace" &&
    !!(obrad?.datum || obrad?.cas || mistoObradu);

  const jeKremaceBezObradu = String(zaklad?.typZakazky || "").trim() === "kremace_bez_obradu";
  const bezObraduScenar = getDefaultBezObraduScenar(prevoz, vstupni);
  const cilovaAdresaKremaceBezObradu =
    prevoz?.cilPrevozuAdresa ||
    automatickaCilovaAdresa ||
    adresaKrematoria ||
    "";


  const datumBase = prevoz.datumPrevozu || obrad?.datum || "";
  const casBase = prevoz.casPrevozu || obrad?.cas || "";
  const mistoOdvozuAdresa = prevoz?.adresaOdvozu || vstupni?.adresaUmrti || "";

  const etapa1Datum = getEtapaValue(prevoz, "etapa1Datum", datumBase);
  const etapa1Cas = getEtapaValue(prevoz, "etapa1Cas", casBase);
  const etapa1Od = getEtapaValue(
    prevoz,
    "etapa1Od",
    String(zaklad?.typZakazky || "").trim() === "kremace_bez_obradu"
      ? FIRMA_ADRESA
      : FIRMA_ADRESA
  );
  const etapa1Kam = getEtapaValue(
    prevoz,
    "etapa1Kam",
    String(zaklad?.typZakazky || "").trim() === "vyjezd"
      ? mistoOdvozuAdresa
      : String(zaklad?.typZakazky || "").trim() === "kremace_bez_obradu"
        ? mistoOdvozuAdresa
        : String(zaklad?.typZakazky || "").trim() === "kremace"
          ? mistoOdvozuAdresa
          : String(zaklad?.typZakazky || "").trim() === "pohreb"
            ? (obrad?.hrbitovAdresa || adresaObradu || "")
            : adresaLednice
  );
  const etapa1Poznamka = getEtapaValue(
    prevoz,
    "etapa1Poznamka",
    String(zaklad?.typZakazky || "").trim() === "vyjezd"
      ? "Firma → místo úmrtí"
      : String(zaklad?.typZakazky || "").trim() === "kremace_bez_obradu"
        ? "Firma → místo odvozu"
        : String(zaklad?.typZakazky || "").trim() === "kremace"
          ? "Firma → místo odvozu / lednice"
          : "Firma → lednice / příprava"
  );

  const etapa2Datum = getEtapaValue(prevoz, "etapa2Datum", obrad?.datum || datumBase);
  const etapa2Cas = getEtapaValue(prevoz, "etapa2Cas", obrad?.cas || casBase);
  const etapa2Od = getEtapaValue(
    prevoz,
    "etapa2Od",
    String(zaklad?.typZakazky || "").trim() === "vyjezd"
      ? mistoOdvozuAdresa
      : String(zaklad?.typZakazky || "").trim() === "kremace_bez_obradu"
        ? mistoOdvozuAdresa
        : FIRMA_ADRESA
  );
  const etapa2Kam = getEtapaValue(
    prevoz,
    "etapa2Kam",
    String(zaklad?.typZakazky || "").trim() === "vyjezd"
      ? (prevoz?.cilPrevozuAdresa || prevoz?.cilPrevozuNazev || "")
      : String(zaklad?.typZakazky || "").trim() === "kremace_bez_obradu"
        ? (adresaKrematoria || "")
        : (adresaObradu || "")
  );
  const etapa2Poznamka = getEtapaValue(
    prevoz,
    "etapa2Poznamka",
    String(zaklad?.typZakazky || "").trim() === "vyjezd"
      ? "Místo úmrtí → uložení"
      : String(zaklad?.typZakazky || "").trim() === "kremace_bez_obradu"
        ? "Místo odvozu → krematorium"
        : "Firma → lednice / příprava → obřad"
  );

  const etapa3Datum = getEtapaValue(prevoz, "etapa3Datum", obrad?.datum || datumBase);
  const etapa3Cas = getEtapaValue(prevoz, "etapa3Cas", obrad?.cas || casBase);
  const etapa3Od = getEtapaValue(
    prevoz,
    "etapa3Od",
    String(zaklad?.typZakazky || "").trim() === "vyjezd"
      ? (prevoz?.cilPrevozuAdresa || prevoz?.cilPrevozuNazev || "")
      : String(zaklad?.typZakazky || "").trim() === "kremace_bez_obradu"
        ? (adresaKrematoria || "")
        : (adresaObradu || "")
  );
  const etapa3Kam = getEtapaValue(
    prevoz,
    "etapa3Kam",
    String(zaklad?.typZakazky || "").trim() === "vyjezd"
      ? (navratNaFirmu ? FIRMA_ADRESA : "")
      : String(zaklad?.typZakazky || "").trim() === "kremace_bez_obradu"
        ? (navratNaFirmu ? FIRMA_ADRESA : "")
        : (adresaKrematoria || cilovaAdresaPodleTypu || "")
  );
  const etapa3Poznamka = getEtapaValue(
    prevoz,
    "etapa3Poznamka",
    String(zaklad?.typZakazky || "").trim() === "vyjezd"
      ? "Uložení → firma"
      : String(zaklad?.typZakazky || "").trim() === "kremace_bez_obradu"
        ? "Krematorium → firma"
        : (navratNaFirmu ? "Obřad → krematorium → firma" : "Obřad → krematorium")
  );


  const etapa1Km = Number(prevoz?.etapy?.etapa1Km || 0) || 0;
  const etapa2Km = Number(prevoz?.etapy?.etapa2Km || 0) || 0;
  const etapa3Km = Number(prevoz?.etapy?.etapa3Km || 0) || 0;
  const etapyKmCelkem = etapa1Km + etapa2Km + etapa3Km;

  const vypocetPcrKm =
    jeSoudniPitva
      ? etapyKmCelkem
      : 0;

  const vypocetPojistovnaKmZaklad =
    jeZdravotniPitva
      ? etapa2Km + etapa3Km
      : 0;

  const pojistovnaKoeficient =
    String(prevoz?.kmRozpad?.pojistovnaKoeficient || "").trim();

  const vypocetPojistovnaKmVypocet =
    jeZdravotniPitva && pojistovnaKoeficient
      ? `${vypocetPojistovnaKmZaklad} × ${pojistovnaKoeficient}`
      : (jeZdravotniPitva ? String(vypocetPojistovnaKmZaklad) : "");

  const vypocetRodinaKm =
    jeSoudniPitva
      ? 0
      : jeZdravotniPitva
        ? 0
        : etapyKmCelkem;

  const cilTyp =
    prevoz.cilPrevozuTyp ||
    (jeSoudniPitva || jeZdravotniPitva ? "pitva_patologie" : "");

  useEffect(() => {
    const typZakazky = String(zaklad?.typZakazky || "").trim();
    const prubehKey = String(zaklad?.prubehKey || "").trim();
    const scenarUmrti = String(zaklad?.scenarUmrti || "").trim();

    if (!typZakazky) return;

    const preset = getTransportPresetFromPrubeh({
      typZakazky,
      prubehKey,
      scenarUmrti,
    });

    if (!preset || !Object.keys(preset).length) return;

    const patch = {};

    for (const [key, value] of Object.entries(preset)) {
      if (!String(prevoz?.[key] || "").trim() && String(value || "").trim()) {
        patch[key] = value;
      }
    }

    if (scenarUmrti === "wagnerka" && String(prevoz?.coolingScenario || "").trim() !== "wagnerka") {
      patch.coolingScenario = "wagnerka";
    }

    if (!Object.keys(patch).length) return;
    patchSection("prevoz", patch);
  }, [
    zaklad?.typZakazky,
    zaklad?.prubehKey,
    zaklad?.scenarUmrti,
    prevoz?.dopravaMode,
    prevoz?.bezObraduScenar,
    prevoz?.cilPrevozuTyp,
    prevoz?.cilPrevozuNazev,
    prevoz?.cilPrevozuAdresa,
    prevoz?.coolingScenario,
    patchSection,
  ]); // AUTO_PREVOZ_FROM_PRUBEH

  const cilInfoLabel =
    String(zaklad?.typZakazky || "").trim() === "pohreb"
      ? "Cíl převzat z hřbitova"
      : String(zaklad?.typZakazky || "").trim() === "kremace" || String(zaklad?.typZakazky || "").trim() === "kremace_bez_obradu"
        ? "Cíl převzat z krematoria"
        : "Cíl převzat z obřadu";

  useEffect(() => {
    const patch = {};

    if (!String(prevoz?.datumPrevozu || "").trim() && String(obrad?.datum || "").trim()) {
      patch.datumPrevozu = String(obrad?.datum || "").trim();
    }

    if (!String(prevoz?.casPrevozu || "").trim() && String(obrad?.cas || "").trim()) {
      patch.casPrevozu = String(obrad?.cas || "").trim();
    }

    if (!Object.keys(patch).length) return;
    patchSection("prevoz", patch);
  }, [
    prevoz?.datumPrevozu,
    prevoz?.casPrevozu,
    obrad?.datum,
    obrad?.cas,
    patchSection,
  ]); // AUTO_PREVOZ_DATUM_CAS

  useEffect(() => {
    if (jeSamostatnyPrevoz) return;

    const typ = String(zaklad?.typZakazky || "").trim();
    const current = prevoz?.etapy || {};
      const mistoOdvozuAdresa = prevoz?.adresaOdvozu || vstupni?.adresaUmrti || "";
      const datumBase = prevoz?.datumPrevozu || obrad?.datum || "";
      const casBase = prevoz?.casPrevozu || obrad?.cas || "";

      const nextEtapy = {
        ...(typ === "vyjezd" ? {
          etapa1Datum: datumBase,
          etapa1Cas: casBase,
          etapa1Od: FIRMA_ADRESA,
          etapa1Kam: mistoOdvozuAdresa || "",
          etapa1Poznamka: "Firma → místo úmrtí",

          etapa2Datum: datumBase,
          etapa2Cas: casBase,
          etapa2Od: mistoOdvozuAdresa || "",
          etapa2Kam: prevoz?.cilPrevozuAdresa || prevoz?.cilPrevozuNazev || "",
          etapa2Poznamka:
            jeSoudniPitva || jeZdravotniPitva
              ? "Místo úmrtí → patologie"
              : "Místo úmrtí → uložení",

          ...(navratNaFirmu ? {
            etapa3Datum: datumBase,
            etapa3Cas: casBase,
            etapa3Od: prevoz?.cilPrevozuAdresa || prevoz?.cilPrevozuNazev || "",
            etapa3Kam: FIRMA_ADRESA,
            etapa3Poznamka:
              jeSoudniPitva || jeZdravotniPitva
                ? "Patologie → firma"
                : "Uložení → firma",
          } : {}),
        } : {}),

        ...(typ === "kremace_bez_obradu" ? {
          etapa1Datum: datumBase,
          etapa1Cas: casBase,
          etapa1Od: FIRMA_ADRESA,
          etapa1Kam: mistoOdvozuAdresa || "",
          etapa1Poznamka: "Firma → místo odvozu",

          etapa2Datum: datumBase,
          etapa2Cas: casBase,
          etapa2Od: mistoOdvozuAdresa || "",
          etapa2Kam: cilovaAdresaKremaceBezObradu || "",
          etapa2Poznamka: "Místo odvozu → krematorium",

          ...(navratNaFirmu ? {
            etapa3Datum: datumBase,
            etapa3Cas: casBase,
            etapa3Od: cilovaAdresaKremaceBezObradu || "",
            etapa3Kam: FIRMA_ADRESA,
            etapa3Poznamka: "Krematorium → firma",
          } : {}),
        } : {}),

        ...(typ === "pohreb" ? {
          etapa1Datum: datumBase,
          etapa1Cas: casBase,
          etapa1Od: FIRMA_ADRESA,
          etapa1Kam: obrad?.hrbitovAdresa || adresaObradu || "",
          etapa1Poznamka: "Firma → lednice / příprava → hřbitov",
        } : {}),

        ...(typ === "kremace" ? {
          etapa1Datum: datumBase,
          etapa1Cas: casBase,
          etapa1Od: FIRMA_ADRESA,
          etapa1Kam: mistoOdvozuAdresa || "",
          etapa1Poznamka: "Firma → místo odvozu / lednice",

          etapa2Datum: obrad?.datum || datumBase,
          etapa2Cas: obrad?.cas || casBase,
          etapa2Od: FIRMA_ADRESA,
          etapa2Kam: adresaObradu || "",
          etapa2Poznamka: "Firma → lednice / příprava → obřad",

          etapa3Datum: obrad?.datum || datumBase,
          etapa3Cas: obrad?.cas || casBase,
          etapa3Od: adresaObradu || "",
          etapa3Kam: adresaKrematoria || "",
          etapa3Poznamka: navratNaFirmu ? "Obřad → krematorium → firma" : "Obřad → krematorium",
        } : {}),
      };

      if (!Object.keys(nextEtapy).length) return;

      const mergedEtapy = { ...(current || {}) };
      let changed = false;

      for (const [key, value] of Object.entries(nextEtapy)) {
        const cur = String((current || {})[key] || "").trim();
        const nxt = String(value || "").trim();
        if (!cur && nxt) {
          mergedEtapy[key] = value;
          changed = true;
        }
      }

      if (!changed) return;

      patchSection("prevoz", {
        etapy: mergedEtapy,
      });
  }, [
    jeSamostatnyPrevoz,
    zaklad?.typZakazky,
    prevoz?.datumPrevozu,
    prevoz?.casPrevozu,
    prevoz?.adresaOdvozu,
    prevoz?.etapy,
    vstupni?.adresaUmrti,
    obrad?.datum,
    obrad?.cas,
    obrad?.hrbitovAdresa,
    adresaObradu,
    adresaKrematoria,
    navratNaFirmu,
    patchSection,
  ]);

  useEffect(() => {
    const currentKmRozpad = prevoz?.kmRozpad || {};
    const currentFakturaceRozpad = prevoz?.fakturaceRozpad || {};

    const nextKmRozpad = {
      ...(currentKmRozpad || {}),
      pcrKm: jeSoudniPitva ? String(vypocetPcrKm) : "",
      pojistovnaKm: jeZdravotniPitva ? String(vypocetPojistovnaKmZaklad) : "",
      rodinaKm: jeSoudniPitva || jeZdravotniPitva ? "" : String(vypocetRodinaKm),
      pojistovnaKmVypocet: vypocetPojistovnaKmVypocet,
    };

    const currentExterniFakturace = prevoz?.externiFakturace || {};
    const currentRodinnaFakturace = prevoz?.rodinnaFakturace || {};

    const nextFakturaceRozpad = {
      ...(currentFakturaceRozpad || {}),
      pcr: {
        ...((currentFakturaceRozpad || {}).pcr || {}),
        aktivni: jeSoudniPitva,
        subjekt: "Policie ČR",
        km: jeSoudniPitva ? String(vypocetPcrKm) : "",
      },
      pojistovna: {
        ...((currentFakturaceRozpad || {}).pojistovna || {}),
        aktivni: jeZdravotniPitva,
        subjekt: String(zaklad?.zdravotniPojistovnaNazev || vyjezd?.zdravotniPojistovnaNazev || "").trim(),
        km: jeZdravotniPitva ? String(vypocetPojistovnaKmZaklad) : "",
        koeficient: String(currentKmRozpad?.pojistovnaKoeficient || ""),
        kmVypocet: vypocetPojistovnaKmVypocet,
      },
      rodina: {
        ...((currentFakturaceRozpad || {}).rodina || {}),
        aktivni: !jeSoudniPitva && !jeZdravotniPitva,
        subjekt: "Rodina",
        km: !jeSoudniPitva && !jeZdravotniPitva ? String(vypocetRodinaKm) : "",
      },
    };

    const nextExterniFakturace = {
      ...(currentExterniFakturace || {}),
      pcr: {
        ...((currentExterniFakturace || {}).pcr || {}),
        aktivni: jeSoudniPitva,
        subjekt: "Policie ČR",
        kmZaklad: jeSoudniPitva ? String(vypocetPcrKm) : "",
        kmVypocet: jeSoudniPitva ? String(vypocetPcrKm) : "",
        castkaBezDph:
          String((((currentExterniFakturace || {}).pcr || {}).castkaBezDph) || "").trim(),
      },
      pojistovna: {
        ...((currentExterniFakturace || {}).pojistovna || {}),
        aktivni: jeZdravotniPitva,
        subjekt: String(zaklad?.zdravotniPojistovnaNazev || vyjezd?.zdravotniPojistovnaNazev || "").trim(),
        kmZaklad: jeZdravotniPitva ? String(vypocetPojistovnaKmZaklad) : "",
        koeficient: String(currentKmRozpad?.pojistovnaKoeficient || ""),
        kmVypocet: vypocetPojistovnaKmVypocet,
        castkaBezDph:
          String((((currentExterniFakturace || {}).pojistovna || {}).castkaBezDph) || "").trim(),
      },
    };

    const nextRodinnaFakturace = {
      ...(currentRodinnaFakturace || {}),
      datum:
        String((currentRodinnaFakturace || {}).datum || "").trim() ||
        String(prevoz?.datumPrevozu || "").trim() ||
        "",
      subjekt: "Rodina",
      km: !jeSoudniPitva && !jeZdravotniPitva ? String(vypocetRodinaKm) : "0",
      castkaBezDph:
        String((currentRodinnaFakturace || {}).castkaBezDph || "").trim(),
    };

    const sameKmRozpad =
      JSON.stringify(currentKmRozpad || {}) === JSON.stringify(nextKmRozpad || {});
    const sameFakturaceRozpad =
      JSON.stringify(currentFakturaceRozpad || {}) === JSON.stringify(nextFakturaceRozpad || {});
    const sameExterniFakturace =
      JSON.stringify(currentExterniFakturace || {}) === JSON.stringify(nextExterniFakturace || {});
    const sameRodinnaFakturace =
      JSON.stringify(currentRodinnaFakturace || {}) === JSON.stringify(nextRodinnaFakturace || {});

    if (sameKmRozpad && sameFakturaceRozpad && sameExterniFakturace && sameRodinnaFakturace) return;

    patchSection("prevoz", {
      kmRozpad: nextKmRozpad,
      fakturaceRozpad: nextFakturaceRozpad,
      externiFakturace: nextExterniFakturace,
      rodinnaFakturace: nextRodinnaFakturace,
    });
  }, [
    jeSoudniPitva,
    jeZdravotniPitva,
    vypocetPcrKm,
    vypocetPojistovnaKmZaklad,
    vypocetPojistovnaKmVypocet,
    vypocetRodinaKm,
    prevoz?.kmRozpad,
    prevoz?.fakturaceRozpad,
    prevoz?.rodinnaFakturace,
    zaklad?.zdravotniPojistovnaNazev,
    vyjezd?.zdravotniPojistovnaNazev,
    patchSection,
  ]);

  useEffect(() => {
    if (jeSamostatnyPrevoz) return;

    const nextEtapy = {
      ...(etapa1Datum ? { etapa1Datum } : {}),
      ...(etapa1Cas ? { etapa1Cas } : {}),
      ...(etapa1Od ? { etapa1Od } : {}),
      ...(etapa1Kam ? { etapa1Kam } : {}),
      ...(etapa1Poznamka ? { etapa1Poznamka } : {}),

      ...(etapa2Datum ? { etapa2Datum } : {}),
      ...(etapa2Cas ? { etapa2Cas } : {}),
      ...(etapa2Od ? { etapa2Od } : {}),
      ...(etapa2Kam ? { etapa2Kam } : {}),
      ...(etapa2Poznamka ? { etapa2Poznamka } : {}),

      ...(etapa3Datum ? { etapa3Datum } : {}),
      ...(etapa3Cas ? { etapa3Cas } : {}),
      ...(etapa3Od ? { etapa3Od } : {}),
      ...(etapa3Kam ? { etapa3Kam } : {}),
      ...(etapa3Poznamka ? { etapa3Poznamka } : {}),
    };

    const current = prevoz?.etapy || {};
    const merged = { ...(current || {}) };
    let changed = false;

    for (const [k, v] of Object.entries(nextEtapy)) {
      const cur = String(merged[k] || "").trim();
      const nxt = String(v || "").trim();
      if (!cur && nxt) {
        merged[k] = v;
        changed = true;
      }
    }

    if (!changed) return;

    patchSection("prevoz", {
      etapy: merged,
    });
  }, [
    jeSamostatnyPrevoz,
    etapa1Datum, etapa1Cas, etapa1Od, etapa1Kam, etapa1Poznamka,
    etapa2Datum, etapa2Cas, etapa2Od, etapa2Kam, etapa2Poznamka,
    etapa3Datum, etapa3Cas, etapa3Od, etapa3Kam, etapa3Poznamka,
    prevoz?.etapy,
    patchSection,
  ]); // AUTO_FILL_ETAPY_FROM_DEFAULTS

  const viditelnaMista = useMemo(() => {
    if (!cilTyp) return CILOVA_MISTA;
    return CILOVA_MISTA.filter((item) => item.typ === cilTyp);
  }, [cilTyp]);

  const handleTargetNameChange = (value) => {
    setField("cilPrevozuNazev", value);

    const found = viditelnaMista.find(
      (item) => normalizeText(item.nazev) === normalizeText(value)
    );

    if (found) {
      setField("cilPrevozuAdresa", found.adresa || "");
    }
  };

  const calcOneKm = async (from, to) => {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const token = readToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await fetch(apiUrl("/api/vypocitej-km"), {
      method: "POST",
      headers,
      body: JSON.stringify({
        umrtiAddress: from,
        toAddress: to,
        routeType: "car_fast",
      }),
    });

    const raw = await res.text();
    let data = null;
    try {
      data = raw ? JSON.parse(raw) : null;
    } catch {
      data = raw;
    }

    if (!res.ok || !data?.ok) {
      throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
    }

    return {
      km: Number(data.kmCeil || data.km || 0) || 0,
      kmRaw: Number(data.kmRaw || 0) || 0,
    };
  };

  const calcKm = async () => {
    const etapa1OdReal = String(prevoz?.etapy?.etapa1Od || "").trim();
    const etapa1KamReal = String(prevoz?.etapy?.etapa1Kam || "").trim();
    const etapa2OdReal = String(prevoz?.etapy?.etapa2Od || "").trim();
    const etapa2KamReal = String(prevoz?.etapy?.etapa2Kam || "").trim();
    const etapa3OdReal = String(prevoz?.etapy?.etapa3Od || "").trim();
    const etapa3KamReal = String(prevoz?.etapy?.etapa3Kam || "").trim();

    const hasAnyStage =
      (etapa1OdReal && etapa1KamReal) ||
      (etapa2OdReal && etapa2KamReal) ||
      (etapa3OdReal && etapa3KamReal);

    if (!hasAnyStage) {
      setKmInfo({ state: "err", msg: "Nejdřív se musí vyplnit etapy převozu." });
      return;
    }

    try {
      setKmInfo({ state: "saving", msg: "Počítám etapy..." });

      const calcStage = async (from, to) => {
        const a = String(from || "").trim();
        const b = String(to || "").trim();

        if (!a || !b) {
          return { km: 0, kmRaw: 0 };
        }

        return await calcOneKm(a, b);
      };

      const etapa1Res = await calcStage(etapa1OdReal, etapa1KamReal);
      const etapa2Res = await calcStage(etapa2OdReal, etapa2KamReal);
      const etapa3Res = await calcStage(etapa3OdReal, etapa3KamReal);

      const etapa1KmCalc = Number(etapa1Res?.km || etapa1Res?.kmCeil || 0) || 0;
      const etapa2KmCalc = Number(etapa2Res?.km || etapa2Res?.kmCeil || 0) || 0;
      const etapa3KmCalc = Number(etapa3Res?.km || etapa3Res?.kmCeil || 0) || 0;
      const kmCelkem = etapa1KmCalc + etapa2KmCalc + etapa3KmCalc;

      patchSection("prevoz", {
        km: String(kmCelkem || 0),
        kmRaw: String(
          (
            Number(etapa1Res?.kmRaw || 0) +
            Number(etapa2Res?.kmRaw || 0) +
            Number(etapa3Res?.kmRaw || 0)
          ).toFixed(2)
        ),
        etapy: {
          ...(prevoz?.etapy || {}),
          etapa1Km: String(etapa1KmCalc),
          etapa2Km: String(etapa2KmCalc),
          etapa3Km: String(etapa3KmCalc),
        },
      });

      setKmInfo({
        state: "ok",
        msg: `Spočítáno: etapa 1 = ${etapa1KmCalc} km, etapa 2 = ${etapa2KmCalc} km, etapa 3 = ${etapa3KmCalc} km. Celkem ${kmCelkem} km.`,
      });
    } catch (e) {
      setKmInfo({
        state: "err",
        msg: String(e?.message || e || "Výpočet etap selhal"),
      });
    }
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 34, fontWeight: 900, color: "#111827", lineHeight: 1.05 }}>Převoz</div>

      {jeSoudniPitva ? (
        <div style={{
          marginBottom: 18,
          padding: 14,
          borderRadius: 16,
          background: "#eff6ff",
          border: "1px solid #bfdbfe",
          color: "#1e3a8a",
          fontWeight: 700,
        }}>
          Režim: soudní pitva. První blok výjezdu se eviduje pro fakturaci PČR.
        </div>
      ) : null}

      {jeZdravotniPitva ? (
        <div style={{
          marginBottom: 18,
          padding: 14,
          borderRadius: 16,
          background: "#ecfeff",
          border: "1px solid #a5f3fc",
          color: "#155e75",
          fontWeight: 700,
        }}>
          Režim: zdravotní pitva. Pojišťovna hradí pouze naložené převozy:
          místo úmrtí → pitva a pitva → místo úmrtí.
        </div>
      ) : null}

          <div style={{ marginTop: 8, color: "#6b7280", fontSize: 16 }}>
            Převoz z místa úmrtí do cílového místa s automatickým výpočtem km.
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 14, color: "#6b7280", fontWeight: 700 }}>Aktivní zakázka</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: "#111827" }}>{activeOrderId || "-"}</div>
        </div>
      </div>

      <div style={infoBoxStyle}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#6b7280", marginBottom: 6 }}>Režim</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
          {jeSamostatnyPrevoz ? "Samostatný převoz" : "Převoz jako součást zakázky"}
        </div>
        <div style={{ marginTop: 8, fontSize: 14, color: "#374151", fontWeight: 700 }}>
          Výchozí bod firmy: {FIRMA_ADRESA}
        </div>
        <div style={{ marginTop: 10, fontSize: 13, color: "#6b7280", lineHeight: 1.5 }}>
          Km se počítají pouze z vyplněných adres. V Obradu musí být ručně zadaná skutečná adresa místa rozloučení a u krematoria skutečná adresa krematoria.
        </div>
      </div>

      <div style={gridStyle}>
        <div>
          <label style={labelStyle}>Místo odvozu</label>
          <input
            type="text"
            value={prevoz.mistoOdvozu || vstupni.mistoUmrti || ""}
            onChange={(e) => setField("mistoOdvozu", e.target.value)}
            placeholder="Např. nemocnice, domov, adresa"
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Adresa odvozu</label>
          <input
            type="text"
            value={odvozAdresa}
            onChange={(e) => setField("adresaOdvozu", e.target.value)}
            placeholder="Adresa místa úmrtí / odvozu"
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Typ cíle</label>
          <select
            value={prevoz.cilPrevozuTyp || ""}
            onChange={(e) => setField("cilPrevozuTyp", e.target.value)}
            style={inputStyle}
          >
            <option value="">Vyber typ cíle</option>
            <option value="pitva">Pitva / patologie</option>
            <option value="chladnice">Chladnice / lednice</option>
            <option value="krematorium">Krematorium</option>
          </select>
        </div>

        <div>
          <label style={labelStyle}>Cílové místo</label>
          <input
            list="pohrebka2-cilova-mista"
            type="text"
            value={prevoz.cilPrevozuNazev || ""}
            onChange={(e) => handleTargetNameChange(e.target.value)}
            placeholder="Začni psát např. Krematorium České..."
            style={inputStyle}
          />
          <datalist id="pohrebka2-cilova-mista">
            {viditelnaMista.map((item) => (
              <option key={`${item.typ}-${item.nazev}`} value={item.nazev} />
            ))}
          </datalist>
        </div>

        <div>
          <label style={labelStyle}>Adresa cíle</label>
          <input
            type="text"
            value={prevoz.cilPrevozuAdresa || ""}
            onChange={(e) => setField("cilPrevozuAdresa", e.target.value)}
            placeholder="Adresa cílového místa"
            style={inputReadonlyStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Datum převozu</label>
          <input
            type="date"
            value={prevoz.datumPrevozu || ""}
            onChange={(e) => setField("datumPrevozu", e.target.value)}
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Čas převozu</label>
          <input
            type="time"
            value={prevoz.casPrevozu || ""}
            onChange={(e) => setField("casPrevozu", e.target.value)}
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Posádka</label>
          <input
            type="text"
            value={prevoz.posadka || ""}
            onChange={(e) => setField("posadka", e.target.value)}
            placeholder="Kdo jel převoz"
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Vozidlo</label>
          <input
            type="text"
            value={prevoz.vozidlo || ""}
            onChange={(e) => setField("vozidlo", e.target.value)}
            placeholder="SPZ nebo název vozidla"
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Km celkem</label>
          <input
            type="number"
            value={prevoz.km || ""}
            onChange={(e) => setField("km", e.target.value)}
            placeholder="0"
            style={inputStyle}
          />
        </div>

        <div style={{ display: "flex", alignItems: "end" }}>
          <label style={{ ...labelStyle, marginBottom: 0, display: "flex", alignItems: "center", gap: 10 }}>
            <input
              type="checkbox"
              checked={navratNaFirmu}
              onChange={(e) => setField("navratNaFirmu", !!e.target.checked)}
            />
            Po dokončení návrat na firmu
          </label>
        </div>

        <div style={{ display: "flex", alignItems: "end" }}>
          <button
            type="button"
            onClick={calcKm}
            style={secondaryBtn}
            disabled={kmInfo.state === "saving"}
          >
            {kmInfo.state === "saving" ? "Počítám..." : "Spočítat etapy"}
          </button>
        </div>
      </div>

      {kmInfo.msg ? (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            borderRadius: 14,
            background: kmInfo.state === "err" ? "#fff1f2" : "#ecfdf5",
            color: kmInfo.state === "err" ? "#9f1239" : "#166534",
            border: kmInfo.state === "err" ? "1px solid #fecdd3" : "1px solid #bbf7d0",
            fontWeight: 700,
          }}
        >
          {kmInfo.msg}
        </div>
      ) : null}

      <div style={{ ...infoBoxStyle, marginTop: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#6b7280", marginBottom: 8 }}>Rozpis etap</div>
        <div style={{ display: "grid", gap: 6, fontSize: 14, color: "#111827" }}>
          <div>Etapa 1: <strong>{etapa1Km} km</strong></div>
          <div>Etapa 2: <strong>{etapa2Km} km</strong></div>
          <div>Etapa 3: <strong>{etapa3Km} km</strong></div>
          <div>Celkem: <strong>{etapyKmCelkem || prevoz.km || 0} km</strong></div>
        </div>
      </div>

      {jeKremaceBezObradu ? (
        <div style={{ ...infoBoxStyle, marginTop: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#6b7280", marginBottom: 10 }}>Scénář kremace bez obřadu</div>
          <div style={{ display: "grid", gap: 10 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 700, color: "#111827" }}>
              <input
                type="radio"
                name="bezObraduScenar"
                checked={bezObraduScenar === "ck_nemocnice"}
                onChange={() => setField("bezObraduScenar", "ck_nemocnice")}
              />
              Úmrtí / převzetí z CK nemocnice
            </label>

            <label style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 700, color: "#111827" }}>
              <input
                type="radio"
                name="bezObraduScenar"
                checked={bezObraduScenar === "doma_nebo_mimo_ck_nemocnici"}
                onChange={() => setField("bezObraduScenar", "doma_nebo_mimo_ck_nemocnici")}
              />
              Úmrtí doma nebo mimo CK nemocnici
            </label>

            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
              CK nemocnice = etapa 1 se nepočítá jako běžný odvoz z místa úmrtí. Doma / mimo CK nemocnici = etapa 1 se počítá standardně.
            </div>
          </div>
        </div>
      ) : null}

      <div style={{ ...infoBoxStyle, marginTop: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#6b7280", marginBottom: 10 }}>Etapy převozu</div>

        <div style={{ display: "grid", gap: 18 }}>
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 14, padding: 14, background: "#fff" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#111827", marginBottom: 10 }}>{jeVyjezd || jeSoudniPitva || jeZdravotniPitva ? "Etapa 1 — Firma → místo úmrtí" : "Etapa 1 — Firma → místo odvozu → lednice"}</div>
            {!jeSamostatnyPrevoz && automatickaCilovaAdresa ? (
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              borderRadius: 14,
              background: "#f8fbff",
              border: "1px solid #dbeafe",
              color: "#1e3a8a",
              fontWeight: 700,
              lineHeight: 1.45,
            }}
          >
            {cilInfoLabel}: {automatickaCilovaAdresa}
          </div>
        ) : null}

        <div style={gridStyle}>
              <div><label style={labelStyle}>Datum</label><input type="date" value={etapa1Datum} onChange={(e) => setEtapaField("etapa1Datum", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Čas</label><input type="time" value={etapa1Cas} onChange={(e) => setEtapaField("etapa1Cas", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Odkud</label><input type="text" value={etapa1Od} onChange={(e) => setEtapaField("etapa1Od", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Kam</label><input type="text" value={etapa1Kam} onChange={(e) => setEtapaField("etapa1Kam", e.target.value)} style={inputStyle} /></div>
            </div>
            <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 180px", gap: 16 }}>
              <div><label style={labelStyle}>Poznámka</label><input type="text" value={etapa1Poznamka} onChange={(e) => setEtapaField("etapa1Poznamka", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Km</label><input type="number" value={etapa1Km || ""} onChange={(e) => setEtapaField("etapa1Km", e.target.value)} style={inputStyle} /></div>
            </div>
          </div>

          <div style={{ border: "1px solid #e5e7eb", borderRadius: 14, padding: 14, background: "#fff" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#111827", marginBottom: 10 }}>{jeVyjezd || jeSoudniPitva || jeZdravotniPitva ? "Etapa 2 — Místo úmrtí → patologie / uložení" : "Etapa 2 — Firma → lednice → obřad"}</div>
            <div style={gridStyle}>
              <div><label style={labelStyle}>Datum</label><input type="date" value={etapa2Datum} onChange={(e) => setEtapaField("etapa2Datum", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Čas</label><input type="time" value={etapa2Cas} onChange={(e) => setEtapaField("etapa2Cas", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Odkud</label><input type="text" value={etapa2Od} onChange={(e) => setEtapaField("etapa2Od", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Kam</label><input type="text" value={etapa2Kam} onChange={(e) => setEtapaField("etapa2Kam", e.target.value)} style={inputStyle} /></div>
            </div>
            <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 180px", gap: 16 }}>
              <div><label style={labelStyle}>Poznámka</label><input type="text" value={etapa2Poznamka} onChange={(e) => setEtapaField("etapa2Poznamka", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Km</label><input type="number" value={etapa2Km || ""} onChange={(e) => setEtapaField("etapa2Km", e.target.value)} style={inputStyle} /></div>
            </div>
          </div>

          <div style={{ border: "1px solid #e5e7eb", borderRadius: 14, padding: 14, background: "#fff" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#111827", marginBottom: 10 }}>{jeVyjezd || jeSoudniPitva || jeZdravotniPitva ? "Etapa 3 — Patologie / uložení → firma" : "Etapa 3 — Obřad → krematorium → firma"}</div>
            <div style={gridStyle}>
              <div><label style={labelStyle}>Datum</label><input type="date" value={etapa3Datum} onChange={(e) => setEtapaField("etapa3Datum", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Čas</label><input type="time" value={etapa3Cas} onChange={(e) => setEtapaField("etapa3Cas", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Odkud</label><input type="text" value={etapa3Od} onChange={(e) => setEtapaField("etapa3Od", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Kam</label><input type="text" value={etapa3Kam} onChange={(e) => setEtapaField("etapa3Kam", e.target.value)} style={inputStyle} /></div>
            </div>
            <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 180px", gap: 16 }}>
              <div><label style={labelStyle}>Poznámka</label><input type="text" value={etapa3Poznamka} onChange={(e) => setEtapaField("etapa3Poznamka", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Km</label><input type="number" value={etapa3Km || ""} onChange={(e) => setEtapaField("etapa3Km", e.target.value)} style={inputStyle} /></div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 16, border: "1px solid #d1d5db", borderRadius: 14, padding: 14, background: "#f9fafb" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#6b7280", marginBottom: 8 }}>Výpočet etap</div>
          <div style={{ display: "grid", gap: 6, fontSize: 14, color: "#111827" }}>
            <div>Etapa 1: <strong>{etapa1Km} km</strong></div>
            <div>Etapa 2: <strong>{etapa2Km} km</strong></div>
            <div>Etapa 3: <strong>{etapa3Km} km</strong></div>
            <div>Celkem: <strong>{etapyKmCelkem} km</strong></div>
          </div>
        </div>
      </div>


      <div style={{
        marginTop: 18,
        padding: 16,
        borderRadius: 18,
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
      }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: "#111827", marginBottom: 12 }}>
          Rozpad kilometrů a fakturace
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          <div style={{ padding: 12, borderRadius: 14, background: "#fff", border: "1px solid #e5e7eb" }}>
            <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 700 }}>PČR</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#111827", marginTop: 6 }}>
              {prevoz?.kmRozpad?.pcrKm || "0"} km
            </div>
          </div>

          <div style={{ padding: 12, borderRadius: 14, background: "#fff", border: "1px solid #e5e7eb" }}>
            <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 700 }}>Pojišťovna</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#111827", marginTop: 6 }}>
              {prevoz?.kmRozpad?.pojistovnaKm || "0"} km
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: "#4b5563" }}>
              Výpočet: {prevoz?.kmRozpad?.pojistovnaKmVypocet || "-"}
            </div>
          </div>

          <div style={{ padding: 12, borderRadius: 14, background: "#fff", border: "1px solid #e5e7eb" }}>
            <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 700 }}>Rodina</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#111827", marginTop: 6 }}>
              {prevoz?.kmRozpad?.rodinaKm || "0"} km
            </div>
          </div>
        </div>

        {jeZdravotniPitva ? (
          <div style={{ marginTop: 14, maxWidth: 280 }}>
            <label style={labelStyle}>Koeficient pojišťovny</label>
            <input
              style={inputStyle}
              value={prevoz?.kmRozpad?.pojistovnaKoeficient || ""}
              onChange={(e) =>
                patchSection("prevoz", {
                  kmRozpad: {
                    ...(prevoz?.kmRozpad || {}),
                    pojistovnaKoeficient: e.target.value,
                  },
                })
              }
              placeholder="např. 1,23"
            />
          </div>
        ) : null}

        <div style={{
          marginTop: 16,
          padding: 14,
          borderRadius: 16,
          background: "#ffffff",
          border: "1px solid #e5e7eb",
        }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#111827", marginBottom: 10 }}>
            Aktivní fakturace tohoto převozu
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700 }}>PČR</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#111827" }}>
                {prevoz?.fakturaceRozpad?.pcr?.aktivni ? "ANO" : "NE"}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700 }}>Pojišťovna</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#111827" }}>
                {prevoz?.fakturaceRozpad?.pojistovna?.aktivni ? "ANO" : "NE"}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700 }}>Rodina</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#111827" }}>
                {prevoz?.fakturaceRozpad?.rodina?.aktivni ? "ANO" : "NE"}
              </div>
            </div>
          </div>
        </div>

        <div style={{
          marginTop: 18,
          padding: 16,
          borderRadius: 18,
          background: "#f9fafb",
          border: "1px solid #e5e7eb",
        }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#111827", marginBottom: 12 }}>
            Rodinná fakturace
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
            <div>
              <label style={labelStyle}>Číslo rodinné faktury</label>
              <input
                style={inputStyle}
                value={prevoz?.rodinnaFakturace?.cislo || ""}
                onChange={(e) =>
                  patchSection("prevoz", {
                    rodinnaFakturace: {
                      ...(prevoz?.rodinnaFakturace || {}),
                      cislo: e.target.value,
                    },
                  })
                }
              />
            </div>

            <div>
              <label style={labelStyle}>Datum rodinné faktury</label>
              <input
                type="date"
                style={inputStyle}
                value={prevoz?.rodinnaFakturace?.datum || ""}
                onChange={(e) =>
                  patchSection("prevoz", {
                    rodinnaFakturace: {
                      ...(prevoz?.rodinnaFakturace || {}),
                      datum: e.target.value,
                    },
                  })
                }
              />
            </div>

            <div>
              <label style={labelStyle}>Km pro rodinu</label>
              <input
                style={readOnlyStyle}
                value={prevoz?.rodinnaFakturace?.km || "0"}
                readOnly
              />
            </div>

            <div>
              <label style={labelStyle}>Částka bez DPH pro rodinu</label>
              <input
                style={inputStyle}
                value={prevoz?.rodinnaFakturace?.castkaBezDph || ""}
                onChange={(e) =>
                  patchSection("prevoz", {
                    rodinnaFakturace: {
                      ...(prevoz?.rodinnaFakturace || {}),
                      castkaBezDph: e.target.value,
                    },
                  })
                }
              />
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>Poznámka k rodinné faktuře</label>
              <textarea
                style={textareaStyle}
                value={prevoz?.rodinnaFakturace?.poznamka || ""}
                onChange={(e) =>
                  patchSection("prevoz", {
                    rodinnaFakturace: {
                      ...(prevoz?.rodinnaFakturace || {}),
                      poznamka: e.target.value,
                    },
                  })
                }
              />
            </div>
          </div>
        </div>

        <div style={{
          marginTop: 18,
          padding: 16,
          borderRadius: 18,
          background: "#f9fafb",
          border: "1px solid #e5e7eb",
        }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#111827", marginBottom: 12 }}>
            Externí fakturace výjezdu
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
            <div style={{ padding: 14, borderRadius: 14, background: "#fff", border: "1px solid #e5e7eb" }}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>Faktura PČR</div>
              <div><strong>Aktivní:</strong> {prevoz?.externiFakturace?.pcr?.aktivni ? "ANO" : "NE"}</div>
              <div><strong>Číslo:</strong> {prevoz?.externiFakturace?.pcr?.cislo || "-"}</div>
              <div><strong>Subjekt:</strong> {prevoz?.externiFakturace?.pcr?.subjekt || "-"}</div>
              <div><strong>Km základ:</strong> {prevoz?.externiFakturace?.pcr?.kmZaklad || "0"}</div>
              <div><strong>Km výpočet:</strong> {prevoz?.externiFakturace?.pcr?.kmVypocet || "0"}</div>
              <div style={{ marginTop: 8 }}>
                <label style={labelStyle}>Částka bez DPH</label>
                <input
                  style={inputStyle}
                  value={prevoz?.externiFakturace?.pcr?.castkaBezDph || ""}
                  onChange={(e) =>
                    patchSection("prevoz", {
                      externiFakturace: {
                        ...(prevoz?.externiFakturace || {}),
                        pcr: {
                          ...((prevoz?.externiFakturace || {}).pcr || {}),
                          castkaBezDph: e.target.value,
                        },
                      },
                    })
                  }
                />
              </div>
              <div><strong>Služby:</strong> výjezd, převoz na pitvu, návrat na firmu</div>
            </div>

            <div style={{ padding: 14, borderRadius: 14, background: "#fff", border: "1px solid #e5e7eb" }}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>Faktura pojišťovna</div>
              <div><strong>Aktivní:</strong> {prevoz?.externiFakturace?.pojistovna?.aktivni ? "ANO" : "NE"}</div>
              <div><strong>Číslo:</strong> {prevoz?.externiFakturace?.pojistovna?.cislo || "-"}</div>
              <div><strong>Subjekt:</strong> {prevoz?.externiFakturace?.pojistovna?.subjekt || "-"}</div>
              <div><strong>Km základ:</strong> {prevoz?.externiFakturace?.pojistovna?.kmZaklad || "0"}</div>
              <div><strong>Koeficient:</strong> {prevoz?.externiFakturace?.pojistovna?.koeficient || "-"}</div>
              <div><strong>Km výpočet:</strong> {prevoz?.externiFakturace?.pojistovna?.kmVypocet || "-"}</div>
              <div style={{ marginTop: 8 }}>
                <label style={labelStyle}>Částka bez DPH</label>
                <input
                  style={inputStyle}
                  value={prevoz?.externiFakturace?.pojistovna?.castkaBezDph || ""}
                  onChange={(e) =>
                    patchSection("prevoz", {
                      externiFakturace: {
                        ...(prevoz?.externiFakturace || {}),
                        pojistovna: {
                          ...((prevoz?.externiFakturace || {}).pojistovna || {}),
                          castkaBezDph: e.target.value,
                        },
                      },
                    })
                  }
                />
              </div>
              <div><strong>Služby:</strong> naložený převoz na pitvu, naložený převoz z pitvy</div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 18 }}>
          <label style={labelStyle}>Poznámka k převozu</label>
          <textarea
            value={prevoz.poznamka || ""}
            onChange={(e) => setField("poznamka", e.target.value)}
            placeholder="Poznámka k trase, režimu nebo zvláštnostem"
            style={textareaStyle}
          />
        </div>
      </div>
    </div>
  );
}
